import {
  useStripe,
  useElements,
  CardNumberElement,
  CardCvcElement,
  CardExpiryElement,
  CardElement,
} from "@stripe/react-stripe-js";
import React, { useState, useMemo, useEffect } from "react";
import { Col, Container, Row } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { Post } from "../../Axios/AxiosFunctions";
import {
  apiHeader,
  BaseURL,
  falsyArray,
  formRegEx,
  formRegExReplacer,
  validateEmail,
} from "../../config/apiUrl";
import { calculateTotalAmount } from "../../handlers/helperFunctions";
import { clearCart } from "../../redux/cart/cart";
import { saveLocation } from "../../redux/common/commonSlice";
import Button from "../../stories/Button/Button";
import CartItem from "../../stories/cartItem/cartItem";
import ConfirmationModal from "../../stories/Confirmation/ConfirmationModal";
import Footer from "../../stories/Footer";
import Header from "../../stories/Header";
import Input from "../../stories/Input";
import NoDataComp from "../../stories/NoDataComp/NoDataComp";

import classes from "./CheckoutPage.module.css";

const CheckoutPage = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  useEffect(() => {
    dispatch(saveLocation(location));
  }, []);

  const navigate = useNavigate();
  const stripe = useStripe();
  const elements = useElements();
  const options = useOptions();

  const cart = useSelector((state) => state?.cartReducer?.cart);
  const cardPrice = useSelector((state) => state?.commonReducer?.cardPrice);
  const token = useSelector((state) => state?.authReducer?.access_token);

  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [telephone, setTelephone] = useState("");

  const [isLoading, setIsLoading] = useState(false);
  const [isConfirmationModalOpen, setIsConfirmationModalOpen] = useState(false);

  const [deliveryAddress, setDeliveryAddress] = useState({
    country: "",
    state: "",
    addressLine1: "",
    addressLine2: "",
    town: "",
    postCode: "",
  });

  //   handleSubmit
  const handleSubmit = async () => {
    if (isLoading) return;

    const params = {
      firstName,
      lastName,
      email,
      telephone,
      ...deliveryAddress,
    };

    delete params["addressLine2"];

    // validate params
    for (let key in params)
      if (falsyArray.includes(params[key]))
        return toast.warn(
          `${key.replace(formRegEx, formRegExReplacer)} can not be empty.`
        );

    params.addressLine2 = deliveryAddress?.addressLine2;

    // validate email
    if (!validateEmail(email))
      return toast.warn("Please enter a valid email address.");

    // check if stripe has loaded
    if (!stripe || !elements) {
      return;
    }
    setIsLoading(true);
    const payload = await stripe.createPaymentMethod({
      type: "card",
      card: elements.getElement(CardNumberElement),
    });
    setIsLoading(false);
    // if error
    if (payload?.error?.message)
      return toast.warn(`${payload?.error?.message}`);
    setIsLoading(false);

    return [true, payload?.paymentMethod?.id, params];
  };

  // handlePlaceOrder
  const handlePlaceOrder = async () => {
    const [status, pmId, params] = await handleSubmit();
    if (status === true) {
      let finalParams = {
        ...params,
        pmId,
        items: cart.map((e) => ({
          ...(typeof e?.cards !== "object" && { cardId: e?._id }),
          ...(typeof e?.cards === "object" && { collectionId: e?._id }),
          quantity: e?.quantity,
        })),
      };

      const url = BaseURL("users/buy");
      const response = await Post(url, finalParams, apiHeader(token));
      if (response !== undefined) {
        dispatch(clearCart());
        toast.success("Order placed successfully.");
        navigate("/my-cards");
      }
    }
  };

  return (
    <>
      <Header />
      <Container className={classes?.container}>
        <h2>Checkout</h2>
        <Row>
          <Col md={8}>
            <div className={classes?.mainLeft}>
              {/* Cart */}
              <h4>Cart</h4>
              <hr />
              <Row className={classes?.myDetails}>
                <div>
                  <Col md={12} className={classes?.cartHead}>
                    <h6>Image</h6>
                    <h6>Name</h6>
                    <h6>Type</h6>
                    <h6>Price</h6>
                    <h6>Quantity</h6>
                  </Col>
                  <div className={classes?.cartItems}>
                    {cart?.map((e) => {
                      return (
                        <CartItem data={e} key={e?._id} cardPrice={cardPrice} />
                      );
                    })}

                    {cart?.length === 0 && (
                      <NoDataComp
                        title={"Please add atleast 1 card in your cart."}
                      />
                    )}
                  </div>
                </div>
              </Row>
              <h4>My Details</h4>
              <hr />
              <Row className={classes?.myDetails}>
                <Col md={6}>
                  <Input
                    placeholder={"First Name"}
                    value={firstName}
                    setter={setFirstName}
                    label={"First Name"}
                    regexType={"number"}
                  />
                </Col>
                <Col md={6}>
                  <Input
                    placeholder={"Last Name"}
                    value={lastName}
                    setter={setLastName}
                    label={"Last Name"}
                    regexType={"number"}
                  />
                </Col>
                <Col md={6}>
                  <Input
                    placeholder={"Email"}
                    value={email}
                    setter={setEmail}
                    label={"Email"}
                    type="email"
                  />
                </Col>
                <Col md={6}>
                  <Input
                    type={"number"}
                    placeholder={"Telephone"}
                    value={telephone}
                    setter={setTelephone}
                    label={"Telephone"}
                  />
                </Col>
              </Row>
              {/* Delivery Detail */}
              <h4>Delivery Address</h4>
              <hr />
              <Row className={classes?.myDetails}>
                <Col md={6}>
                  <Input
                    placeholder={"Country"}
                    value={deliveryAddress?.country}
                    setter={(e) =>
                      setDeliveryAddress((p) => ({ ...p, country: e }))
                    }
                    label={"Country"}
                    regexType={"number"}
                  />
                </Col>
                <Col md={6}>
                  <Input
                    placeholder={"State"}
                    value={deliveryAddress?.state}
                    setter={(e) =>
                      setDeliveryAddress((p) => ({ ...p, state: e }))
                    }
                    label={"State"}
                    regexType={"number"}
                  />
                </Col>
                <Col md={6}>
                  <Input
                    placeholder={"Address Line 1"}
                    value={deliveryAddress?.addressLine1}
                    setter={(e) =>
                      setDeliveryAddress((p) => ({ ...p, addressLine1: e }))
                    }
                    label={"Address Line 1"}
                  />
                </Col>
                <Col md={6}>
                  <Input
                    placeholder={"Address Line 2"}
                    value={deliveryAddress?.addressLine2}
                    setter={(e) =>
                      setDeliveryAddress((p) => ({ ...p, addressLine2: e }))
                    }
                    label={"Address Line 2"}
                  />
                </Col>
                <Col md={6}>
                  <Input
                    placeholder={"Town"}
                    value={deliveryAddress?.town}
                    setter={(e) =>
                      setDeliveryAddress((p) => ({ ...p, town: e }))
                    }
                    label={"Town"}
                  />
                </Col>
                <Col md={6}>
                  <Input
                    placeholder={"Post Code"}
                    value={deliveryAddress?.postCode}
                    setter={(e) =>
                      setDeliveryAddress((p) => ({ ...p, postCode: e }))
                    }
                    label={"Post Code"}
                    regexType="character"
                  />
                </Col>
              </Row>
              {/* Card */}
              <h4>Card Detail</h4>
              <hr />
              <Row>
                <form className={["w-100"].join(" ")}>
                  <Col lg={12} md={3} sm={12}>
                    <label className={classes.cardNumber}>
                      Card number
                      <CardNumberElement options={options} />
                    </label>
                  </Col>
                  <Col lg={12} md={3} sm={12}>
                    <label className={classes.cardNumber}>
                      Expiration date
                      <CardExpiryElement options={options} />
                    </label>
                  </Col>
                  <Col lg={12} md={3} sm={12}>
                    <label className={classes.cardNumber}>
                      CVC
                      <CardCvcElement options={options} />
                    </label>
                  </Col>
                </form>
              </Row>
            </div>
          </Col>
          <Col md={4}>
            {cart?.length > 0 && (
              <div className={classes?.mainRight}>
                <h3>Total</h3>
                <hr />

                <div className={classes?.itemRow}>
                  <h5>Total Items</h5>
                  <h5>{cart?.length}</h5>
                </div>
                <div className={classes?.itemRow}>
                  <h5>Your Total</h5>
                  <h5>
                    $
                    {parseFloat(calculateTotalAmount(cart, cardPrice)).toFixed(
                      2
                    )}
                  </h5>
                </div>
                <Button
                  primary
                  label={isLoading ? "Please Wait..." : "Order Now"}
                  onClick={async () => {
                    const [status] = await handleSubmit();
                    console.log(status, " status");
                    if (status === true) setIsConfirmationModalOpen(true);
                  }}
                  disabled={cart?.length === 0 || isLoading}
                />
              </div>
            )}
          </Col>
        </Row>
      </Container>
      <Footer />

      <ConfirmationModal
        show={isConfirmationModalOpen}
        setShow={setIsConfirmationModalOpen}
        subTitle={`Do you want to place your order?`}
        onClick={handlePlaceOrder}
        isApiCall={isLoading}
      />
    </>
  );
};

export default CheckoutPage;

const useOptions = () => {
  const options = useMemo(
    () => ({
      style: {
        base: {
          color: "#424770",
          letterSpacing: "0.025em",
          fontFamily: "Source Code Pro, monospace",
          "::placeholder": {
            color: "#aab7c4",
          },
        },
        invalid: {
          color: "#9e2146",
        },
      },
    }),
    []
  );

  return options;
};
